import React from 'react'
import Eyebrow from '../ds/Eyebrow'
import { ArrowLeftIcon } from '../ds/icons'

/**
 * The head of a step in a multi-step screen.
 *
 * Three lines, smallest first: where you are, what is being asked, and what
 * the answer does. The order is the point — the eye reads down the left edge
 * and never has to cross the card to find out which step it is on, which is
 * what a position parked on the right-hand side used to cost.
 *
 * It replaced a stepper widget, and does the same job in a third of the
 * height: three steps with titles of their own do not need a band above them
 * repeating those titles as dots.
 *
 * Sticky by default. This block is the only thing on screen that says which
 * step you are on, and these steps scroll — a repository list runs well past
 * the fold, and losing the header at the top of it is losing the answer to
 * "what am I doing here?" exactly when the list stops being self-evident.
 */
const StepHeader = ({ step, totalSteps, title, children, onBack, sticky = false }: {
  step: number
  totalSteps: number
  title: string
  /** Renders a back link above everything else. Absent means there is nowhere
   * to go back to — which is a real state here, not an oversight: once the app
   * exists, walking back into the form offers a Create button that would make
   * a second one. */
  onBack?: () => void
  /** The line under the title. Prose on the first step; on later ones it is
   * what the step is operating on — the repository, the image — which is the
   * same slot answering the same question with a fact instead of a sentence. */
  children?: React.ReactNode
  sticky?: boolean
}) => (
  <div
    // Two shapes for the same head.
    //
    // On desktop it is a bare block on the page ground, above whatever cards
    // the step renders — painting its own surface there is what made the whole
    // step read as one box with a lid.
    //
    // On mobile it is a solid band that runs the full width, the same
    // treatment the container screens give their context header. It bleeds
    // past the wizard's 1rem gutter with `-mx-4`, which is exactly that
    // gutter.
    className={[
      '-mx-4 px-4 pt-3 pb-4 bg-navy-800 border-b border-navy-500',
      // On desktop the band is transparent, so what separates the head from
      // the card under it is this padding and nothing else — at zero the
      // question and the answer to it shared an edge.
      'lg:mx-0 lg:px-1 lg:pt-0 lg:pb-6 lg:bg-transparent lg:border-b-0',
      sticky ? 'sticky top-0 z-10' : '',
    ].join(' ')}
  >
    {/* Position as a word and as a shape: the count reads, the bar is glanced
        at. Neither is a stepper widget repeating the step titles.

        On desktop the title joins them, so the whole head is one line under
        the screen's own: where you are and what is being asked read left to
        right instead of stacking three headings down the page. On mobile it
        wraps, and the back arrow shares the row — a 44px touch target with no
        label, which keeps the head short on a phone.

        Desktop has no Back here: the head of the screen carries it, named. */}
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5 lg:gap-2.5">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="b4-focus lg:hidden w-11 h-11 flex-none -ml-2.5 -my-2 rounded-control text-navy-200 flex items-center justify-center hover:bg-navy-600"
        >
          <ArrowLeftIcon size={18} />
        </button>
      )}
      <div className="flex items-center gap-1">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div
            key={i}
            className={`h-[3px] rounded-full ${i === step - 1 ? 'w-[22px]' : 'w-3'} ${i <= step - 1 ? 'bg-action' : 'bg-navy-500'}`}
          />
        ))}
      </div>
      <Eyebrow>Step {step} of {totalSteps}</Eyebrow>
      </div>

      {/* Its own line, always. Beside the step count it fitted at most widths
          and wrapped at the rest, so the head was one line or two depending on
          how long the step happened to be called — and a heading that moves is
          worse than a heading that sits one row lower.

          One notch under the screen's own title: at the same 20px the step
          heading and the page heading were two titles of equal rank arguing
          about which one names the screen. */}
      <h2 className="m-0 font-sora text-ds-15 font-semibold tracking-[-0.01em] leading-[1.3] text-white">
        {title}
      </h2>
    </div>
    {children && (
      <div className="mt-2.5 text-ds-135 leading-[1.5] text-navy-200">
        {children}
      </div>
    )}
  </div>
)

export default StepHeader
